import { FieldArray, Formik } from 'formik';
import Toggle from '../../../../components/toggle/Toggle';
import classNames from 'classnames';
import type { Dispatch, SetStateAction } from 'react';
import React, { useEffect } from 'react';
import Selector from '../../../../components/selector/Selector';
import Textarea from '../../../../components/input/Textarea';
import ButtonIcon from '../../../../components/button/Button.icon';
import { GLOBAL_ICONS, QUERIES_IDS } from '../../../../util/vars';
import Button from '../../../../components/button/Button';
import Tabs from '../../../../components/tabs/Tabs';
import * as Yup from 'yup';
import type { OptionType } from '../../../../util/types';
import type { PreviewData } from './EmailPreview';
import type { PutSchoolWeeklyLeadersEmailRequestBody, WeeklyLeadersEmail } from '@whizz/api-model';
import { useI18n } from '@whizz/react-i18n';
import { DateTime, Info } from 'luxon';
import {
  getNextWeekday,
  getPrevWeekday,
  timezoneWeekdayAndHourToUtc,
  utcWeekdayAndHourToTimeZone,
} from '../../../../util/dates';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { repository } from '@whizz/api-client';
import MaterialIcon from '../../../../components/icon/Icon.material';
import type { TFunction } from 'i18next';
import { ChoiceGroup } from '../../../../components';

const VALIDATION_SCHEMA = (t: TFunction) => Yup.object().shape({
  isEnabled: Yup.boolean().defined(),
  hour: Yup.mixed().required(t('forms.errors.required')),
  weekday: Yup.mixed().required(t('forms.errors.required')),
  to: Yup.array().of(Yup.mixed<OptionType<number>>()),
  cc: Yup.array().of(Yup.object().shape({
    label: Yup.string(),
    value: Yup.string().email(),
  })),
  bcc: Yup.array().of(Yup.object().shape({
    label: Yup.string(),
    value: Yup.string().email(),
  })),
  isEnabledOneOffMessage: Yup.boolean(),
  standardGreeting: Yup.boolean(),
  standardMessage: Yup.string().required(t('forms.errors.required')),
  oneOffMessage: Yup.string()
    .when('isEnabledOneOffMessage', {
      is: true,
      then: (schema) => schema.required(t('forms.errors.required')),
    })
  ,
  isEnabledLearningOutcomeReminderMessage: Yup.boolean(),
  learningOutcomeReminderMessage: Yup.string(),
  reportingPeriodFrom: Yup.mixed().required(t('forms.errors.required')),
  reportingPeriodTo: Yup.mixed().required(t('forms.errors.required')),
  metricsComponents: Yup.array().of(Yup.mixed<OptionType<string>>()),
});

const METRICS_COMPONENTS = [
  'usage',
  'progressions',
];

const DEFAULT_VALUES = {
  hour: 8,
  weekday: 1,
  reportingPeriodFrom: 1,
  reportingPeriodTo: 7,
  metricsComponents: METRICS_COMPONENTS as WeeklyLeadersEmail['metricsComponents'],
};

const WEEKDAYS_OPTIONS = Info.weekdays().map((weekday, index) => ({ label: weekday, value: index + 1 }));

export default function EmailConfigurationForm({ weeklyLeadersEmail, setPreviewData, onBackToTable }: Props) {
  const { translate: t } = useI18n();
  const queryClient = useQueryClient();

  const now = DateTime.now().setZone(weeklyLeadersEmail.school.timezone);

  const getSchoolTeachersQuery = useQuery({
    queryKey: [ QUERIES_IDS.ADMIN.GET_SCHOOL_TEACHERS, { id: weeklyLeadersEmail.school.id } ],
    queryFn: () => repository.AdminRepository.getSchoolTeachers(weeklyLeadersEmail.school.id),
  });

  const putSchoolWeeklyLeadersEmailMutation = useMutation({
    mutationFn: ({ data }: {
      data: PutSchoolWeeklyLeadersEmailRequestBody
    }) => repository.AdminRepository.putSchoolWeeklyLeadersEmail(weeklyLeadersEmail.school.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ QUERIES_IDS.ADMIN.GET_WEEKLY_LEADERS_EMAILS ] });
      onBackToTable();
    },
  });

  const schoolLocalWeekdayAndHour = null !== weeklyLeadersEmail.weekday && null !== weeklyLeadersEmail.hour
    ? utcWeekdayAndHourToTimeZone(weeklyLeadersEmail.weekday, weeklyLeadersEmail.hour, weeklyLeadersEmail.school.timezone)
    : null
  ;

  return (
    <div className={'flex flex-col gap-5'}>
      <div>
        <Button
          type={'button'}
          isOutline

          onClick={() => onBackToTable()}
        >
          <MaterialIcon
            icon={'ChevronLeft'}
          />
          Change school
        </Button>
      </div>

      <span className={'text-xl font-bold'}>
        {weeklyLeadersEmail.school.name}, {weeklyLeadersEmail.school.postcode}, ID: {weeklyLeadersEmail.school.id}
      </span>

      <hr className="h-px my-4 bg-gray-300 border-0" />

      <Formik<FormValues>
        validationSchema={VALIDATION_SCHEMA(t)}
        initialValues={{
          isEnabled: weeklyLeadersEmail.isEnabled ?? false,
          hour: null != schoolLocalWeekdayAndHour
            ? {
              label: `${schoolLocalWeekdayAndHour.hour}:00`,
              value: schoolLocalWeekdayAndHour.hour,
            }
            : {
              label: `${DEFAULT_VALUES.hour}:00`,
              value: DEFAULT_VALUES.hour,
            },
          weekday: null !== schoolLocalWeekdayAndHour
            ? {
              label: Info.weekdays()[schoolLocalWeekdayAndHour.weekday - 1],
              value: schoolLocalWeekdayAndHour.weekday,
            }
            : {
              label: Info.weekdays()[DEFAULT_VALUES.weekday - 1],
              value: DEFAULT_VALUES.weekday,
            },
          to: weeklyLeadersEmail.teachers?.map((teacher) => ({
            label: `${teacher.firstname} ${teacher.lastname}`,
            value: teacher.id,
            firstName: teacher.firstname,
          })) ?? [],
          cc: weeklyLeadersEmail.cc?.map((item) => ({ label: item, value: item })) ?? [],
          bcc: weeklyLeadersEmail.bcc?.map((item) => ({ label: item, value: item })) ?? [],
          messageTab: (weeklyLeadersEmail.isEnabledOneOffMessage ?? false) ? MessageTab.oneOff : MessageTab.standard,
          isEnabledOneOffMessage: weeklyLeadersEmail.isEnabledOneOffMessage ?? false,
          standardGreeting: weeklyLeadersEmail.standardGreeting ?? true,
          standardMessage: weeklyLeadersEmail.standardMessage ?? t('pages.ADMIN.FORM.INITIAL_VALUES.STANDARD_MESSAGE'),
          oneOffMessage: weeklyLeadersEmail.oneOffMessage ?? '',
          isEnabledLearningOutcomeReminderMessage: weeklyLeadersEmail.isEnabledLearningOutcomeReminderMessage ?? false,
          learningOutcomeReminderMessage: weeklyLeadersEmail.learningOutcomeReminderMessage ?? '',
          reportingPeriodFrom: null !== weeklyLeadersEmail.reportingPeriodFrom
            ? {
              label: Info.weekdays()[weeklyLeadersEmail.reportingPeriodFrom - 1],
              value: weeklyLeadersEmail.reportingPeriodFrom,
            }
            : {
              label: Info.weekdays()[DEFAULT_VALUES.reportingPeriodFrom - 1],
              value: DEFAULT_VALUES.reportingPeriodFrom,
            },
          reportingPeriodTo: null !== weeklyLeadersEmail.reportingPeriodTo
            ? {
              label: Info.weekdays()[weeklyLeadersEmail.reportingPeriodTo - 1],
              value: weeklyLeadersEmail.reportingPeriodTo,
            }
            : {
              label: Info.weekdays()[DEFAULT_VALUES.reportingPeriodTo - 1],
              value: DEFAULT_VALUES.reportingPeriodTo,
            },
          metricsComponents: (
            null != weeklyLeadersEmail.id
              ? weeklyLeadersEmail.metricsComponents
              : DEFAULT_VALUES.metricsComponents
          ).map((metricsComponent) => ({
            label: t(`pages.ADMIN.FORM.LABELS.METRICS_COMPONENTS_OPTIONS.${metricsComponent}`),
            value: metricsComponent,
          })),
        }}
        onSubmit={(values) => {
          const {
            isEnabled,
            hour,
            weekday,
            to,
            cc,
            bcc,
            isEnabledOneOffMessage,
            standardGreeting,
            standardMessage,
            oneOffMessage,
            isEnabledLearningOutcomeReminderMessage,
            learningOutcomeReminderMessage,
            reportingPeriodFrom,
            reportingPeriodTo,
            metricsComponents,
          } = values;

          const utcWeekdayHour = timezoneWeekdayAndHourToUtc(weekday!.value, hour!.value, weeklyLeadersEmail.school.timezone);

          putSchoolWeeklyLeadersEmailMutation.mutate({
            data: {
              isEnabled: isEnabled,
              hour: utcWeekdayHour.hour,
              weekday: utcWeekdayHour.weekday,
              to: to.map((item) => item.value),
              cc: cc.map((item) => item.value),
              bcc: bcc.map((item) => item.value),
              isEnabledOneOffMessage: isEnabledOneOffMessage,
              standardGreeting: standardGreeting,
              standardMessage: standardMessage!,
              oneOffMessage: oneOffMessage,
              isEnabledLearningOutcomeReminderMessage: isEnabledLearningOutcomeReminderMessage,
              learningOutcomeReminderMessage: learningOutcomeReminderMessage!,
              reportingPeriodFrom: reportingPeriodFrom!.value,
              reportingPeriodTo: reportingPeriodTo!.value,
              metricsComponents: metricsComponents
                .filter((metricsComponent) => null != metricsComponent)
                .map((metricsComponent) => metricsComponent!.value),
            },
          });
        }}
      >
        {({ values, errors, setFieldValue, handleSubmit }) => {
          let nextWeekday: DateTime | null = null;
          let reportingPeriodFromDate: DateTime | null = null;
          let reportingPeriodToDate: DateTime | null = null;

          if (null != values.weekday && null !== values.hour) {
            let dateFrom = now;

            if (null != weeklyLeadersEmail.pausedUntil) {
              const pausedUntil = DateTime.fromISO(weeklyLeadersEmail.pausedUntil);

              if (now < pausedUntil) {
                dateFrom = pausedUntil;
              }
            }

            nextWeekday = getNextWeekday(values.weekday.value, values.hour.value, dateFrom);

            if (null != values.reportingPeriodFrom && null != values.reportingPeriodTo) {
              reportingPeriodToDate = getPrevWeekday(values.reportingPeriodTo.value, 23, nextWeekday);
              reportingPeriodFromDate = getPrevWeekday(values.reportingPeriodFrom.value, 0, reportingPeriodToDate);
            }
          }

          useEffect(() => {
            setPreviewData((prev) => ({
              ...prev,
              message: values.isEnabledOneOffMessage ? values.oneOffMessage : values.standardMessage,
            }));
          }, [ values.standardMessage, values.oneOffMessage, values.isEnabledOneOffMessage ]);

          useEffect(() => {
            if (null === reportingPeriodFromDate || null === reportingPeriodToDate) {
              setPreviewData((prev) => ({
                ...prev,
                startDate: null,
                endDate: null,
              }));

              return;
            }

            setPreviewData((prev) => ({
              ...prev,
              startDate: reportingPeriodFromDate ?? null,
              endDate: reportingPeriodToDate ?? null,
            }));
          }, [ reportingPeriodFromDate?.toLocaleString(), reportingPeriodToDate?.toLocaleString() ]);

          useEffect(() => {
            setPreviewData((prev) => ({
              ...prev,
              metricsComponents: values.metricsComponents.filter((option) => null != option).map((option) => option!.value),
            }));
          }, [ values.metricsComponents ]);

          return (
            <form onSubmit={handleSubmit}>
              <div className={'flex flex-col gap-5'}>
                <div>
                  <Button
                    type={'submit'}
                    color={'positive'}
                    className={'w-full'}

                    disabled={putSchoolWeeklyLeadersEmailMutation.isLoading}
                  >
                    {t('pages.ADMIN.FORM.LABELS.SAVE')}
                  </Button>
                </div>

                <hr className="h-px my-4 bg-gray-300 border-0" />

                <div className={'flex gap-10'}>
                  <div className={'flex gap-3 items-center'}>
                    <Toggle
                      id={'enabled'}
                      label={t('pages.ADMIN.FORM.LABELS.ENABLED')}
                      color={'header'}
                      checked={values.isEnabled}
                      isOnOff
                      onChange={() => {
                        const newValue = !values.isEnabled;

                        setFieldValue('isEnabled', newValue);
                        setPreviewData((prev) => ({
                          ...prev,
                          isEnabled: newValue,
                        }));
                      }}
                    />
                    <button
                      type={'button'}
                      className={'appearance-none'}
                      onClick={() => {
                        const newValue = !values.isEnabled;

                        setFieldValue('isEnabled', newValue);
                        setPreviewData((prev) => ({
                          ...prev,
                          isEnabled: newValue,
                        }));
                      }}
                    >
                      <div className={`font-bold transition-opacity ${classNames({ 'opacity-50': !values.isEnabled })}`}>
                        {t('pages.ADMIN.FORM.LABELS.ENABLED')}
                      </div>
                    </button>
                  </div>
                  {(values.isEnabled && null != nextWeekday) && (
                    <div>
                      <span className={'text-sm'}>
                        Next email goes at {nextWeekday.toFormat('HH:mm')} on {nextWeekday.toLocaleString()}, school local time
                      </span>
                    </div>
                  )}
                </div>

                <div className={'grid grid-cols-2 gap-5'}>
                  <Selector
                    id={'hour'}
                    label={t('pages.ADMIN.FORM.LABELS.HOUR')}

                    options={Array.from(Array(24).keys()).map((hour) => ({ label: `${hour}:00`, value: hour }))}
                    onChange={(option: OptionType<number>) => setFieldValue('hour', option)}
                    value={values.hour ?? ''}
                    error={errors.hour}
                  />
                  <Selector
                    id={'weekday'}
                    label={t('pages.ADMIN.FORM.LABELS.WEEKDAY')}

                    options={WEEKDAYS_OPTIONS}
                    onChange={(option: OptionType<number>) => setFieldValue('weekday', option)}
                    value={values.weekday ?? ''}
                    error={errors.weekday}
                  />
                </div>

                <Selector
                  id={'to'}
                  label={t('pages.ADMIN.FORM.LABELS.TO')}
                  placeholder={t('pages.ADMIN.FORM.PLACEHOLDERS.TO')}
                  options={(getSchoolTeachersQuery.data?.data ?? []).map((teacher) => ({
                    label: `${teacher.firstName} ${teacher.lastName}`,
                    value: teacher.id,
                    firstName: teacher.firstName,
                  }))}
                  onChange={(options: (OptionType<number> & { firstName: string })[]) => {
                    setFieldValue('to', options);

                    setPreviewData((prev) => ({
                      ...prev,
                      teachers: options.map((option) => option.firstName),
                    }));
                  }}
                  value={values.to}
                  error={errors.to}
                  isDisabled={getSchoolTeachersQuery.isFetching}
                  isMulti
                />

                <Selector
                  id={'cc'}
                  label={t('pages.ADMIN.FORM.LABELS.CC')}
                  placeholder={t('pages.ADMIN.FORM.PLACEHOLDERS.CC')}
                  onChange={(options: OptionType<string>[]) => setFieldValue('cc', options)}
                  value={values.cc}
                  error={0 < (errors.cc?.length ?? 0) ? t('pages.ADMIN.FORM.ERRORS.INVALID_EMAILS') : null}
                  customComponents={{
                    DropdownIndicator: () => null,
                    IndicatorSeparator: () => null,
                  }}
                  isCreatable
                  isMulti
                />

                <Selector
                  id={'bcc'}
                  label={t('pages.ADMIN.FORM.LABELS.BCC')}
                  placeholder={t('pages.ADMIN.FORM.PLACEHOLDERS.BCC')}
                  onChange={(options: OptionType<string>[]) => setFieldValue('bcc', options)}
                  value={values.bcc}
                  error={0 < (errors.bcc?.length ?? 0) ? t('pages.ADMIN.FORM.ERRORS.INVALID_EMAILS') : null}
                  customComponents={{
                    DropdownIndicator: () => null,
                    IndicatorSeparator: () => null,
                  }}
                  isCreatable
                  isMulti
                />

                <hr className="h-px my-4 bg-gray-300 border-0" />

                <h3 className={'font-bold'}>
                  {t('pages.ADMIN.FORM.LABELS.YOUR_GREETING')}
                </h3>

                <div className="space-y-3">
                  <ChoiceGroup
                    name={'standardGreeting'}
                    id={'standard'}
                    type={'radio'}
                    label={t('pages.ADMIN.FORM.LABELS.STANDARD_GREETING.standard')}
                    checked={values.standardGreeting === true}
                    onChange={() => {
                      setFieldValue('standardGreeting', true);
                      setPreviewData((prev) => ({
                        ...prev,
                        standardGreeting: true,
                      }));
                    }}
                  />
                  <ChoiceGroup
                    name={'standardGreeting'}
                    id={'custom'}
                    type={'radio'}
                    label={t('pages.ADMIN.FORM.LABELS.STANDARD_GREETING.custom')}
                    checked={values.standardGreeting === false}
                    onChange={() => {
                      setFieldValue('standardGreeting', false);
                      setPreviewData((prev) => ({
                        ...prev,
                        standardGreeting: false,
                      }));
                    }}
                  />
                </div>

                <hr className="h-px my-4 bg-gray-300 border-0" />

                <h3 className={'font-bold'}>
                  {t('pages.ADMIN.FORM.LABELS.YOUR_MESSAGE')}
                </h3>

                <div className={'flex gap-10'}>
                  <div className={'flex gap-3 items-center'}>
                    <Toggle
                      id={'isEnabledOneOffMessage'}
                      label={t('pages.ADMIN.FORM.LABELS.USE_ONE_OFF')}
                      color={'header'}
                      isOnOff
                      onChange={() => {
                        const newValue = !values.isEnabledOneOffMessage;

                        setFieldValue('isEnabledOneOffMessage', newValue);
                        setFieldValue('messageTab', !newValue ? 0 : 1);

                        setPreviewData((prev) => ({
                          ...prev,
                          message: newValue ? values.oneOffMessage : values.standardMessage,
                        }));
                      }}
                      checked={values.isEnabledOneOffMessage}
                    />
                    <button
                      type={'button'}
                      className={'appearance-none'}
                      onClick={() => setFieldValue('isEnabledOneOffMessage', !values.isEnabledOneOffMessage)}
                    >
                      <div className={`font-bold transition-opacity ${classNames({ 'opacity-50': !values.isEnabledOneOffMessage })}`}>
                        {t('pages.ADMIN.FORM.LABELS.USE_ONE_OFF')}
                      </div>
                    </button>
                  </div>
                </div>

                <Tabs
                  id={'messageTabs'}
                  label={'Tabs'}
                  tabs={[
                    {
                      content: t('pages.ADMIN.FORM.LABELS.STANDARD_MESSAGE'),
                    },
                    {
                      content: t('pages.ADMIN.FORM.LABELS.ONE_OFF_MESSAGE'),
                    },
                  ]}
                  active={values.messageTab}
                  handleClick={(value) => setFieldValue('messageTab', value)}
                />

                {MessageTab.standard === values.messageTab
                  ? (
                    <Textarea
                      key={'standardMessage'}
                      id={'standardMessage'}
                      label={t('pages.ADMIN.FORM.LABELS.STANDARD_MESSAGE')}
                      hideLabel
                      placeholder={t('pages.ADMIN.FORM.PLACEHOLDERS.STANDARD_MESSAGE')}
                      onChange={(event) => setFieldValue('standardMessage', event.target.value)}
                      value={values.standardMessage ?? ''}
                      error={errors.standardMessage}
                    />
                  )
                  : (
                    <Textarea
                      key={'oneOffMessage'}
                      id={'oneOffMessage'}
                      label={t('pages.ADMIN.FORM.LABELS.ONE_OFF_MESSAGE')}
                      hideLabel
                      placeholder={t('pages.ADMIN.FORM.PLACEHOLDERS.ONE_OFF_MESSAGE')}
                      onChange={(event) => setFieldValue('oneOffMessage', event.target.value)}
                      value={values.oneOffMessage ?? ''}
                      error={errors.oneOffMessage}
                    />
                  )}

                <hr className="h-px my-4 bg-gray-300 border-0" />

                <h3 className={'font-bold'}>
                  {t('pages.ADMIN.FORM.LABELS.JUST_A_REMINDER')}
                </h3>

                <div className={'flex gap-10'}>
                  <div className={'flex gap-3 items-center'}>
                    <Toggle
                      id={'isEnabledLearningOutcomeReminderMessage'}
                      label={t('pages.ADMIN.FORM.LABELS.INCLUDE_REMINDER')}
                      color={'header'}
                      isOnOff
                      onChange={() => {
                        const newValue = !values.isEnabledLearningOutcomeReminderMessage;

                        setFieldValue('isEnabledLearningOutcomeReminderMessage', newValue);

                        setPreviewData((prev) => ({
                          ...prev,
                          reminderMessage: newValue ? values.learningOutcomeReminderMessage : null,
                        }));
                      }}
                      checked={values.isEnabledLearningOutcomeReminderMessage}
                    />
                    <button
                      type={'button'}
                      className={'appearance-none'}
                      onClick={() => {
                        const newValue = !values.isEnabledLearningOutcomeReminderMessage;

                        setFieldValue('isEnabledLearningOutcomeReminderMessage', newValue);

                        setPreviewData((prev) => ({
                          ...prev,
                          reminderMessage: newValue ? values.learningOutcomeReminderMessage : null,
                        }));
                      }}
                    >
                      <div className={`font-bold transition-opacity ${classNames({ 'opacity-50': !values.isEnabledLearningOutcomeReminderMessage })}`}>
                        {t('pages.ADMIN.FORM.LABELS.INCLUDE_REMINDER')}
                      </div>
                    </button>
                  </div>
                </div>

                <Textarea
                  id={'learningOutcomeReminderMessage'}
                  label={t('pages.ADMIN.FORM.LABELS.REMINDER_MESSAGE')}
                  hideLabel
                  placeholder={t('pages.ADMIN.FORM.PLACEHOLDERS.REMINDER_MESSAGE')}
                  onChange={(event) => {
                    const newValue = event.target.value;

                    setFieldValue('learningOutcomeReminderMessage', newValue);

                    if (!values.isEnabledLearningOutcomeReminderMessage) {
                      return;
                    }

                    setPreviewData((prev) => ({
                      ...prev,
                      reminderMessage: newValue,
                    }));
                  }}
                  value={values.learningOutcomeReminderMessage ?? ''}
                  error={errors.learningOutcomeReminderMessage}
                />

                <hr className="h-px my-4 bg-gray-300 border-0" />

                <h3 className={'font-bold'}>
                  {t('pages.ADMIN.FORM.LABELS.METRICS_COMPONENTS')}
                </h3>

                <div className={'flex flex-col gap-1'}>
                  <div className={'grid grid-cols-2 gap-5'}>
                    <Selector
                      id={'reportingPeriodFrom'}
                      label={t('pages.ADMIN.FORM.LABELS.DATA_PERIOD_FROM')}
                      options={WEEKDAYS_OPTIONS}
                      onChange={(option: OptionType<number> | null) => setFieldValue('reportingPeriodFrom', option)}
                      value={values.reportingPeriodFrom ?? ''}
                      error={errors.reportingPeriodFrom}
                    />

                    <Selector
                      id={'reportingPeriodTo'}
                      label={t('pages.ADMIN.FORM.LABELS.DATA_PERIOD_TO')}
                      options={WEEKDAYS_OPTIONS}
                      onChange={(option: OptionType<number> | null) => setFieldValue('reportingPeriodTo', option)}
                      value={values.reportingPeriodTo ?? ''}
                      error={errors.reportingPeriodTo}
                    />
                  </div>

                  {(null !== reportingPeriodFromDate && null !== reportingPeriodToDate) && (
                    <div className={'text-sm italic'}>
                      {t('pages.ADMIN.FORM.LABELS.NEXT_EMAIL_PERIOD', { from: reportingPeriodFromDate.toLocaleString(), to: reportingPeriodToDate.toLocaleString() })}
                    </div>
                  )}
                </div>

                <div className={'mt-5 flex flex-col gap-5'}>
                  <FieldArray name={'metricsComponents'}>
                    {({ push, replace, remove }) => {
                      return (
                        <>
                          {values.metricsComponents.map((component, index) => (
                            <div
                              key={index}
                              className={'mt-2 flex items-center'}
                            >
                              <div className={'flex-grow'}>
                                <span>
                                  {t('pages.ADMIN.FORM.LABELS.METRIC_COMPONENT', { number: index + 1 })}
                                </span>
                              </div>
                              <div className={'flex-grow'}>
                                <Selector
                                  id={`metricsComponent[${index}]`}
                                  label={t('pages.ADMIN.FORM.LABELS.METRIC_COMPONENT', { number: index + 1 })}
                                  hideLabel
                                  isClearable={false}
                                  options={METRICS_COMPONENTS
                                    .filter((metricsComponent) => !values.metricsComponents.some((option) => metricsComponent === option?.value))
                                    .map((metricsComponent) => ({
                                      label: t(`pages.ADMIN.FORM.LABELS.METRICS_COMPONENTS_OPTIONS.${metricsComponent}`),
                                      value: metricsComponent,
                                    }))
                                  }
                                  onChange={(option: OptionType<number>) => replace(index, option)}
                                  value={values.metricsComponents[index] ?? ''}
                                  error={errors.metricsComponents?.[index]}
                                />
                              </div>
                              <ButtonIcon
                                type={'button'}
                                icon={GLOBAL_ICONS.delete}
                                size={'sm'}
                                title={t('pages.ADMIN.FORM.LABELS.DELETE_METRIC_COMPONENT', { number: index + 1 })}
                                onClick={() => remove(index)}
                                asLink
                              />
                            </div>
                          ))}

                          <Button
                            type={'button'}
                            className={'w-full'}
                            isOutline

                            onClick={() => push(null)}
                            disabled={values.metricsComponents.length >= METRICS_COMPONENTS.length}
                          >
                            {t('pages.ADMIN.FORM.LABELS.ADD_METRIC_COMPONENT')}
                          </Button>
                        </>
                      );
                    }}
                  </FieldArray>
                </div>

                <hr className="h-px my-4 bg-gray-300 border-0" />

                <div>
                  <Button
                    type={'submit'}
                    color={'positive'}
                    className={'w-full'}

                    disabled={putSchoolWeeklyLeadersEmailMutation.isLoading}
                  >
                    {t('pages.ADMIN.FORM.LABELS.SAVE')}
                  </Button>
                </div>
              </div>
            </form>
          );
        }}
      </Formik>
    </div>
  );
}


interface Props {
  weeklyLeadersEmail: WeeklyLeadersEmail;
  setPreviewData: Dispatch<SetStateAction<PreviewData>>;
  onBackToTable: () => void;
}

enum MessageTab {
  standard,
  oneOff,
}

interface FormValues extends Pick<WeeklyLeadersEmail, 'standardMessage' | 'oneOffMessage' | 'learningOutcomeReminderMessage'>{
  hour: OptionType<NonNullable<WeeklyLeadersEmail['hour']>> | null;
  weekday: OptionType<NonNullable<WeeklyLeadersEmail['weekday']>> | null;
  to: OptionType<number>[];
  cc: OptionType<string>[];
  bcc: OptionType<string>[];
  isEnabledOneOffMessage: NonNullable<WeeklyLeadersEmail['isEnabledOneOffMessage']>;
  standardGreeting: NonNullable<WeeklyLeadersEmail['standardGreeting']>;
  isEnabled: NonNullable<WeeklyLeadersEmail['isEnabled']>;
  isEnabledLearningOutcomeReminderMessage: NonNullable<WeeklyLeadersEmail['isEnabledLearningOutcomeReminderMessage']>;

  reportingPeriodFrom: OptionType<NonNullable<WeeklyLeadersEmail['reportingPeriodFrom']>> | null;
  reportingPeriodTo: OptionType<NonNullable<WeeklyLeadersEmail['reportingPeriodTo']>> | null;
  metricsComponents: (OptionType<WeeklyLeadersEmail['metricsComponents'][number]> | null)[];

  // Internal
  messageTab: MessageTab;
}
